import React, { createContext, useState, useContext, ReactNode, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  setAuthenticated,
  setRole,
  resetRole,
  setUserInfo,
} from '../redux/actions/authAction';

import { logoutApiCall } from './apicall/authApi';
import { authService } from './authServices'; // Import authService
import SessionExpiryPrompt from '../components/SessionExpiryPrompt';
import { CONFIG } from './config';

const AuthContext = createContext<any>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const dispatch = useDispatch<any>();
  const navigate = useNavigate();

  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(() => !authService.isTokenExpired());
  const [role, setRoleState] = useState<string>(() => {
    const userRole = authService.getUserInfo()?.role;
    return Array.isArray(userRole) ? userRole.join(', ') : userRole || '';
  });
  const [remainingTime, setRemainingTime] = useState<number | null>(null);
  const [showRefreshPrompt, setShowRefreshPrompt] = useState<boolean>(false);

  // Synchronize Redux state with auth state
  useEffect(() => {
    if (isAuthenticated) {
      dispatch(setAuthenticated(true));
      dispatch(setRole(role));
    } else {
      dispatch(setAuthenticated(false));
      dispatch(resetRole());
    }
  }, [isAuthenticated, role, dispatch]);

  // Calculate and update remaining time
  useEffect(() => {
    const updateRemainingTime = () => {
      const expiration = authService.getTokenExpiration();

      if (expiration) {
        const timeLeft = Math.max(expiration - Date.now(), 0);

        if (timeLeft <= 0) {
          console.log('Session expired. Logging out...');
          setShowRefreshPrompt(false);
          authService.clearToken();
          setIsAuthenticated(false);
          setRoleState('');
          dispatch(resetRole());
         
          //navigate(CONFIG.urls.getLoginUrl());
      
        } else {
          setRemainingTime(timeLeft);
          //console.log(timeLeft);
          // Show prompt when less than 1 minute (60000 ms) remaining
          if (timeLeft <= 60000 && !showRefreshPrompt) {
            setShowRefreshPrompt(true);
          }
          // Automatically dismiss the prompt if time increases (more than 1 minute remaining)
          if (timeLeft > 60001 && showRefreshPrompt) {
            setShowRefreshPrompt(false);
          }
        }
      } else {
        setRemainingTime(null);
        setShowRefreshPrompt(false);
      }
    };

    updateRemainingTime(); // Initial calculation
    const interval = setInterval(updateRemainingTime, 1000); // Update every second

    return () => clearInterval(interval); // Cleanup on unmount
  }, [dispatch, navigate, showRefreshPrompt]);

  // Handler for the refresh button
  const handleRefreshToken = async () => {
    const refreshedExpiration = await authService.refreshAuthToken();
    if (refreshedExpiration) {
      setRemainingTime(refreshedExpiration - Date.now());
      setShowRefreshPrompt(false); // Dismiss the prompt on successful refresh
    } else {
      authService.clearToken();
      setIsAuthenticated(false);
      setRoleState('');
      dispatch(resetRole());
      //navigate(CONFIG.urls.getLoginUrl());
    }
  };

  // Handler for the dismiss button
  const handleDismiss = () => {
    setShowRefreshPrompt(false);
  };

  // Manual token refresh method
  const refreshTokenManually = async () => {
    try {
      const refreshedExpiration = await authService.refreshAuthToken();
      if (refreshedExpiration) {
        setRemainingTime(refreshedExpiration - Date.now());
       // console.log(refreshedExpiration - Date.now());
        console.log('Token refreshed manually.');
      } else {
        console.error('Failed to refresh token manually.');
      }
    } catch (error) {
      console.error('An error occurred during manual token refresh:', error);
    }
  };

  const loginauth = async (responseUserData: any) => {
    const userdata = responseUserData;
    const roleNames = Array.isArray(userdata.role)
      ? userdata.role.join(', ')
      : userdata.role;

    authService.setUserInfo({ email: userdata.email, name: userdata.name, role: roleNames });

    setRoleState(roleNames);
    setIsAuthenticated(true);
    dispatch(setUserInfo({ name: userdata.name, email: userdata.email }));
  };

  const logout = async () => {
    if (!isAuthenticated) {
      console.warn('User not authenticated.');
      return;
    }

    try {
      const response = await logoutApiCall();
      if (response.status === 200) {
        authService.clearToken();
        setIsAuthenticated(false);
        setRoleState('');
        dispatch(resetRole());
      } else {
        console.error('Logout failed:', response.data.message || 'Unknown error');
      }
    } catch (error) {
      console.error('An error occurred during logout:', error);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        role,
        remainingTime,
        loginauth,
        logout,
        refreshTokenManually,
      }}
    >
      {children}
      {showRefreshPrompt && (
        <SessionExpiryPrompt
          remainingTime={remainingTime}
          onRefresh={handleRefreshToken}
          onDismiss={handleDismiss}
        />
      )}
    </AuthContext.Provider>
  );
};

export default AuthProvider;
